<!-- live-map.component.html -->

<div class="lm">

  <!-- ── Free plan locked overlay ─────────────────── -->
  <div class="lm__locked-overlay" *ngIf="!loadingPlan && isLocked">
    <div class="lm__locked-card">
      <div class="lm__locked-icon">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="11" width="18" height="11" rx="2"
                stroke="currentColor" stroke-width="1.6"/>
          <path d="M7 11V7C7 4.2 9.2 2 12 2C14.8 2 17 4.2 17 7V11"
                stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
          <circle cx="12" cy="16" r="1.5" fill="currentColor"/>
        </svg>
      </div>
      <h2 class="lm__locked-title">Live Map is Locked</h2>
      <p class="lm__locked-desc">
        Real-time GPS tracking is available on <strong>Starter</strong> and higher plans.<br/>
        You are currently on the <strong>{{ currentPlan }}</strong> plan.
      </p>
      <div class="lm__locked-features">
        <div class="lm__locked-feature">
          <span class="lm__locked-check">✓</span>
          Track all workers in real time
        </div>
        <div class="lm__locked-feature">
          <span class="lm__locked-check">✓</span>
          View GPS route history & replay
        </div>
        <div class="lm__locked-feature">
          <span class="lm__locked-check">✓</span>
          Online / offline alerts
        </div>
      </div>
      <button class="lm__locked-btn" (click)="promptUpgrade()">
        Upgrade to Unlock Live Map
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path d="M5 12H19M13 6L19 12L13 18"
                stroke="currentColor" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>
  </div>

  <!-- ── Page Header ───────────────────────────────── -->
  <div class="lm__header">
    <div class="lm__header-left">
      <div>
        <h1 class="lm__title">
          <span class="lm__live-badge">
            <span class="lm__live-dot"></span>
            LIVE
          </span>
          Worker Map
        </h1>
        <p class="lm__subtitle">Real-time GPS positions — auto-refreshes every 10 seconds.</p>
      </div>
    </div>

    <div class="lm__header-right">
      <!-- Route Replay toggle -->
      <button class="lm__replay-btn"
              [class.lm__replay-btn--active]="showReplayPanel"
              (click)="toggleReplayPanel()">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <polygon points="5 3 19 12 5 21 5 3"
                   stroke="currentColor" stroke-width="2"
                   stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        Route Replay
      </button>

      <!-- Refresh countdown -->
      <div class="lm__countdown" title="Next refresh in…">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
          <polyline points="12 6 12 12 16 14" stroke="currentColor" stroke-width="1.8"
                    stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span>{{ countdown }}s</span>
      </div>

      <!-- Manual refresh -->
      <button class="lm__refresh-btn" (click)="manualRefresh()" [class.lm__refresh-btn--spinning]="refreshing">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
          <path d="M3 12C3 7 7.03 3 12 3C15.69 3 18.88 5.06 20.5 8"
                stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
          <path d="M21 12C21 17 16.97 21 12 21C8.31 21 5.12 18.94 3.5 16"
                stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
          <polyline points="20.5 3 20.5 8 15.5 8" stroke="currentColor" stroke-width="1.8"
                    stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        Refresh
      </button>
    </div>
  </div>

  <!-- ── Stats Strip ─────────────────────────────────── -->
  <div class="lm__strip">
    <div class="lm__strip-item">
      <span class="lm__strip-dot lm__strip-dot--online"></span>
      <span class="lm__strip-val">{{ onlineCount }}</span>
      <span class="lm__strip-label">Online</span>
    </div>
    <div class="lm__strip-sep"></div>
    <div class="lm__strip-item">
      <span class="lm__strip-dot lm__strip-dot--offline"></span>
      <span class="lm__strip-val">{{ offlineCount }}</span>
      <span class="lm__strip-label">Offline</span>
    </div>
    <div class="lm__strip-sep"></div>
    <div class="lm__strip-item">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style="color:var(--fo-muted)">
        <path d="M12 2C8.1 2 5 5.1 5 9C5 14.2 12 22 12 22C12 22 19 14.2 19 9C19 5.1 15.9 2 12 2Z"
              stroke="currentColor" stroke-width="1.8"/>
        <circle cx="12" cy="9" r="2.5" stroke="currentColor" stroke-width="1.8"/>
      </svg>
      <span class="lm__strip-val">{{ totalWorkers }}</span>
      <span class="lm__strip-label">Total Tracked</span>
    </div>
    <div class="lm__strip-sep"></div>
    <div class="lm__strip-item">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style="color:var(--fo-teal)">
        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
        <polyline points="12 6 12 12 16 14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
      </svg>
      <span class="lm__strip-val">{{ lastUpdated }}</span>
      <span class="lm__strip-label">Last Updated</span>
    </div>
  </div>

  <!-- ── Main Body ─────────────────────────────────── -->
  <div class="lm__body">

    <!-- Worker Sidebar -->
    <div class="lm__sidebar">

      <div class="lm__sidebar-head">
        <h3 class="lm__sidebar-title">Workers</h3>
        <span class="lm__sidebar-count">{{ liveWorkers.length }}</span>
      </div>

      <!-- Empty -->
      <div class="lm__sidebar-empty" *ngIf="liveWorkers.length === 0 && !loading">
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
          <path d="M12 2C8.1 2 5 5.1 5 9C5 14.2 12 22 12 22C12 22 19 14.2 19 9C19 5.1 15.9 2 12 2Z"
                stroke="currentColor" stroke-width="1.4"/>
          <circle cx="12" cy="9" r="2.5" stroke="currentColor" stroke-width="1.4"/>
        </svg>
        <p>No workers tracked yet.</p>
      </div>

      <!-- Loading skeleton -->
      <div class="lm__worker-skeleton" *ngIf="loading">
        <div class="lm__skeleton-row" *ngFor="let i of [1,2,3]"></div>
      </div>

      <!-- Worker list -->
      <div class="lm__worker-list" *ngIf="!loading">
        <div
          class="lm__worker-row"
          *ngFor="let w of liveWorkers"
          [class.lm__worker-row--active]="selectedWorkerId === w.userId"
          (click)="selectWorker(w)">

          <div class="lm__worker-avatar" [style.background]="getAvatarColor(w.userId)">
            {{ getInitials(getWorkerName(w)) }}
          </div>

          <div class="lm__worker-info">
            <span class="lm__worker-name">{{ getWorkerName(w) }}</span>
            <span class="lm__worker-time">{{ getLastSeenText(w) }}</span>
          </div>

          <span class="lm__worker-status"
                [class.lm__worker-status--online]="isWorkerOnline(w)"
                [class.lm__worker-status--offline]="!isWorkerOnline(w)">
          </span>

        </div>
      </div>

      <!-- Legend -->
      <div class="lm__legend">
        <div class="lm__legend-item">
          <span class="lm__legend-dot lm__legend-dot--online"></span>
          Online (SignalR Connected)
        </div>
        <div class="lm__legend-item">
          <span class="lm__legend-dot lm__legend-dot--offline"></span>
          Offline
        </div>
        <div class="lm__legend-item" *ngIf="selectedWorkerId">
          <span class="lm__legend-line"></span>
          Route history
        </div>
      </div>

      <!-- Clear history button -->
      <button class="lm__clear-btn" *ngIf="selectedWorkerId" (click)="clearHistory()">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
          <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
          <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
        </svg>
        Clear route
      </button>

    </div>

    <!-- Map -->
    <div class="lm__map-wrap">
      <div id="map" class="lm__map"></div>

      <!-- Map loading overlay -->
      <div class="lm__map-loading" *ngIf="loading">
        <div class="lm__map-spinner"></div>
        <span>Loading live positions…</span>
      </div>

      <!-- ── Route Replay Panel ─────────────────────── -->
      <div class="lm__replay-panel" *ngIf="showReplayPanel && !isLocked">

        <div class="lm__replay-head">
          <h3 class="lm__replay-title">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <polygon points="5 3 19 12 5 21 5 3" fill="currentColor"/>
            </svg>
            Route Replay
          </h3>
          <button class="lm__replay-close" (click)="closeReplayPanel()">×</button>
        </div>

        <!-- Worker selector -->
        <div class="lm__replay-field">
          <label class="lm__replay-label">Select Worker</label>
          <select class="lm__replay-select"
                  [(ngModel)]="replayWorkerId"
                  (change)="onReplayWorkerSelected()">
            <option value="">— Choose worker —</option>
            <option *ngFor="let w of liveWorkers" [value]="w.userId">
              {{ getWorkerName(w) }}
            </option>
          </select>
        </div>

        <!-- Date selector -->
        <div class="lm__replay-field" *ngIf="availableDates.length > 0">
          <label class="lm__replay-label">Select Date</label>
          <select class="lm__replay-select" [(ngModel)]="replayDate">
            <option value="">— Choose date —</option>
            <option *ngFor="let d of availableDates" [value]="d">
              {{ d | date:'EEEE, dd MMM yyyy':'UTC' }}
            </option>
          </select>
        </div>

        <div class="lm__replay-empty"
             *ngIf="replayWorkerId && !loadingDates && availableDates.length === 0">
          No route history available for this worker.
        </div>

        <!-- Load route button -->
        <button class="lm__replay-load-btn"
                (click)="loadRoute()"
                [disabled]="!replayWorkerId || !replayDate || loadingRoute">
          <span *ngIf="loadingRoute">Loading…</span>
          <span *ngIf="!loadingRoute">Load Route</span>
        </button>

        <!-- Route info + playback controls -->
        <div class="lm__replay-controls" *ngIf="routePointCount > 0">

          <div class="lm__replay-info">
            <span>{{ routePointCount }} GPS points</span>
            <span>{{ replayWorkerName }}</span>
          </div>

          <!-- Progress bar -->
          <div class="lm__replay-progress-wrap">
            <div class="lm__replay-progress-bar" [style.width.%]="replayProgress"></div>
          </div>
          <div class="lm__replay-progress-text">{{ replayProgress }}%</div>

          <!-- Speed control -->
          <div class="lm__replay-field">
            <label class="lm__replay-label">Replay Speed</label>
            <select class="lm__replay-select" [(ngModel)]="replaySpeed">
              <option [value]="0.5">0.5× (Slow)</option>
              <option [value]="1">1× (Normal)</option>
              <option [value]="2">2× (Fast)</option>
              <option [value]="5">5× (Very Fast)</option>
            </select>
          </div>

          <!-- Playback buttons -->
          <div class="lm__replay-btns">
            <button class="lm__replay-ctrl-btn" (click)="resetReplay()" title="Reset to start">
              ⏮
            </button>
            <button class="lm__replay-ctrl-btn lm__replay-ctrl-btn--play"
                    (click)="isReplaying ? stopReplay() : startReplay()">
              {{ isReplaying ? '⏸ Pause' : '▶ Play' }}
            </button>
          </div>

        </div>
      </div>

    </div>
  </div>

</div>